import { isDev } from "shared/util/EnvironmentHelpers";
import LocalEnvironment from "shared/util/LocalEnvironment";

// This is where JS keeps all the good security stuff 🔒
const crypto = window.crypto;
const cryptoSubtle = crypto && crypto.subtle;

// AesKeyGenParams
const AES_KEY_GEN = {
	name: "AES-CBC",
	length: 128,
};

// RsaOaepParams
const RSA_OAEP = {
	name: "RSA-OAEP",
};

// RsaHashedImportParams
const RSA_HASHED_IMPORT = {
	name: "RSA-OAEP",
	hash: "SHA-256",
};

// The RSA Public Key to use for encryption.
const PEM_ENCODED_PUBLIC_KEY = isDev && LocalEnvironment.getEnvironment().includes(".dev")
	? `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEApjZjIedsII0WeA9UYMRu
+kyxiH8twYn4KsVr9Uo/twQ5U/0H4m37hU+/ju/twjxtIVK+2RUIvZ53DBn/oNxo
1MtatIEXSyjWG2/A1RPp1mhLhoAtTNvtNaoce6Qr1N3i1QuQjtzPE4Z5xpc0byyW
1yejmwYnu35CYWFvK0WKv8jb5jJ26igNDaG08FyoeKai8MX5zfaf9EMpbgr00FCS
wdnwMB7w6Lv3sGCSejyEEMleGtURCBAR5efw4AB6RAqKjuh8gAEQGWtr5x9IVRYD
t2al5+ryAbQ/yxjTYqh5vYJzeA21/EgBAFssiyxhVZmTsbW3CTfM49Xuz/rnuAgT
kwIDAQAB
-----END PUBLIC KEY-----`
	: `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAyK5V/P77h8lqG7cxz7UK
w7Gk/bRnm5b+JHz4g5a1ZHt63M3GsqylHjBQ/YvVx0wRz0o6mKH8AOJYXDash1hX
KApqkTjU3z+EYx3nz9853pJGUDF3ZSHz+Hjjqj2GG4l4rrGsaUoYUMBoYZfoujzO
gRecbi7/XxRLmU6m23/oBlru9sJBPwX2JpFRIVuLn42oOt4Dj1Lrfe6c6AYXTMf6
pvh56o0XqvRZ0IUZCRhVPVKHgENnSRgadShRe2E1MFfNWczQ+K2kwYoPrXs7M7bQ
4fEbvNTvH+pPLd7qBdawRFOkgqUO5hP47mCCeDu/l6mcRnecYvzu6cZQA15HQuyK
KQIDAQAB
-----END PUBLIC KEY-----`;

/**
 * Encodes a string to an array buffer.
 * From: https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
 * @param {string} str - The string to encode.
 * @returns {ArrayBuffer} encoded string.
 */
export function str2ab(str) {
	const buf = new ArrayBuffer(str.length);
	const bufView = new Uint8Array(buf);
	// eslint-disable-next-line no-plusplus, no-loops/no-loops
	for (let i = 0, strLen = str.length; i < strLen; i++) {
		bufView[i] = str.charCodeAt(i);
	}

	return buf;
}

/**
 * Decodes a string from an array buffer.
 * From: https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
 * @param {string} buf - The array buffer to decode.
 * @returns {ArrayBuffer} string.
 */
export function ab2str(buf) {
	return String.fromCharCode.apply(null, new Uint8Array(buf));
}

/**
 * Imports a PEM-encoded RSA public key as a CryptoKey.
 * @param {string} pem - A PEM-encoded RSA public key.
 * @param {RsaHashedImportParams} algorithm - The algorithm to use for importing.
 * @returns {Promise<CryptoKey>} The CryptoKey representing the public key.
 */
export const importRSAPublicKey = (pem, algorithm = RSA_HASHED_IMPORT) => {
	// Fetch the part of the PEM string between header and footer
	const pemHeader = "-----BEGIN PUBLIC KEY-----";
	const pemFooter = "-----END PUBLIC KEY-----";
	const pemContents = pem.substring(pemHeader.length, pem.length - pemFooter.length).trim();

	// Base64 decode the string to get the binary data
	const binaryDerString = atob(pemContents);

	// Convert from a binary string to an ArrayBuffer
	const binaryDer = str2ab(binaryDerString);

	const format = "spki";
	const extractable = false;
	const keyUsages = ["wrapKey", "encrypt"];
	return cryptoSubtle.importKey(
		format,
		binaryDer,
		algorithm,
		extractable,
		keyUsages,
	);
};

/**
 * Generate an AES IV.
 * @param {number} length - The desired IV length.
 * @returns {Uint8Array} An array of cryptographically random numbers.
 */
export const generateAESIV = (length) => {
	return crypto.getRandomValues(new Uint8Array(length));
};

/**
 * Generate an AES key.
 * @param {AesKeyGenParams} algorithm - The algorithm to use for generating.
 * @returns {Promise<CryptoKey>} A JavaScript Promise resolving to an AES key.
 */
export const generateAESKey = (algorithm = AES_KEY_GEN) => {
	const extractable = true;
	const keyUsages = ["encrypt", "decrypt"];
	return cryptoSubtle.generateKey(
		algorithm,
		extractable,
		keyUsages,
	);
};

/**
 * Export and encrypt an AES key with an RSA key.
 * @param {CryptoKey} aesKey - An AES key.
 * @param {CryptoKey} rsaPublicKey - An RSA key.
 * @param {RsaOaepParams} algorithm - The algorithm to use for wrapping.
 * @returns {Promise<ArrayBuffer>} The RSA-encrypted AES key.
 */
export const encryptAESKeyWithRSA = async (aesKey, rsaPublicKey, algorithm = RSA_OAEP) => {
	const format = "raw";
	return cryptoSubtle.wrapKey(
		format,
		aesKey,
		rsaPublicKey,
		algorithm,
	);
};

/**
 * Encrypt a string with an AES key.
 * @param {string} string - A string to encrypt.
 * @param {CryptoKey} aesKey - An AES key.
 * @param {CryptoKey} aesIv - An AES IV.
 * @returns {Promise<ArrayBuffer>} The AES-encrypted string.
 */
export const encryptStringWithAES = (string, aesKey, aesIv) => {
	// AesCbcParams
	const AES_CBC = {
		name: "AES-CBC",
		iv: aesIv,
	};

	return cryptoSubtle.encrypt(
		AES_CBC,
		aesKey,
		new Uint8Array(str2ab(string)),
	);
};

/**
 * Create an encoded payload to transmit to the backend.
 * @param {Uint8Array} encryptedMessage - An encrypted message.
 * @param {Uint8Array} encryptedKey - An encrypted AES key.
 * @param {Uint8Array} aesIv - An AES IV.
 * @returns {Promise<string>} The base64 encoded payload.
 */
export const createEncodedPayload = (encryptedMessage, encryptedKey, aesIv) => {
	// Length of array needed to hold parameters.
	const totalLength = aesIv.length +
		encryptedMessage.length +
		encryptedKey.length;

	// Create new array for parameters.
	const concatenatedParameters = new Uint8Array(totalLength);

	// Add to array in the following order:
	// 1) the initialization vector for the AES cipher
	// 2) the encrypted message
	// 3) the encrypted AES key
	concatenatedParameters.set(aesIv);
	concatenatedParameters.set(encryptedMessage, aesIv.length);
	concatenatedParameters.set(encryptedKey, aesIv.length + encryptedMessage.length);

	// Convert parameter buffer to a string
	const parameterString = ab2str(concatenatedParameters.buffer);

	// Use base64 to encode the parameters.
	const encodedParameterString = btoa(parameterString);

	return encodedParameterString;
};

/**
 * Encrypts a given JavaScript object into a string.
 * @param {Object} object - The JavaScript object to encrypt. Must be valid JSON.
 * @returns {Promise<string>} The encrypted blob string.
 */
async function encryptObject(object = {}) {
	if (!object || typeof object !== "object") {
		throw new Error("TypeError: Failed to parse.");
	}

	// Stringify the object.
	const rawMessage = JSON.stringify(object);

	// Generate a random AES IV 16 bytes in length.
	const aesIv = generateAESIV(16);

	// Generate a random AES key 16 bytes in length.
	const aesKey = await generateAESKey();

	// Create an AES cipher using this key and encrypt the message.
	const encryptedMessage = new Uint8Array(await encryptStringWithAES(
		rawMessage,
		aesKey,
		aesIv,
	));

	// Create an RSA cipher using the RSA public key and encrypt the aes key.
	const rsaPublicKey = await importRSAPublicKey(PEM_ENCODED_PUBLIC_KEY);
	const encryptedKey = new Uint8Array(await encryptAESKeyWithRSA(
		aesKey,
		rsaPublicKey,
	));

	// Concatenate parameters + use base64 to encode them into a payload.
	return createEncodedPayload(
		encryptedMessage,
		encryptedKey,
		aesIv,
	);
}

export default encryptObject;



// WEBPACK FOOTER //
// ./src/shared/util/encryptObject.js